// TableOfContents.tsx
import React, { useEffect, useState } from "react"
import { cn } from "../lib/utils"

interface TableContentProps {
  items: Array<{ id: string; title: string; level: number }>
  className?: string
}

const TableContent: React.FC<TableContentProps> = ({ items, className }) => {
  const [activeId, setActiveId] = useState<string>("")

  useEffect(() => {
    const handleScroll = () => {
      const offsets = items
        .map(item => {
          const el = document.getElementById(item.id)
          if (el) {
            const rect = el.getBoundingClientRect()
            return { id: item.id, top: rect.top, bottom: rect.bottom }
          }
          return null
        })
        .filter(offset => offset !== null) as Array<{ id: string; top: number; bottom: number }>

      if (offsets.length === 0) return

      // Threshold để xác định khi nào section tiếp theo nên được highlight (150px từ đầu màn hình)
      const threshold = 150

      let activeSectionId: string | null = null

      // Tìm section đang hiển thị trên màn hình (top >= 0) và gần đầu màn hình nhất
      const visibleSections = offsets.filter(offset => offset.top >= 0)

      if (visibleSections.length > 0) {
        // Tìm section đang hiển thị và gần đầu màn hình nhất (top nhỏ nhất)
        const closestVisible = visibleSections.reduce((prev, curr) => (curr.top < prev.top ? curr : prev))

        // Nếu section gần nhất có top < threshold, highlight section đó
        if (closestVisible.top < threshold) {
          activeSectionId = closestVisible.id
        } else {
          // Nếu section gần nhất xa đầu màn hình (top >= threshold),
          // tìm section đã đi qua gần nhất để highlight
          const passedSections = offsets.filter(offset => offset.top < 0)
          if (passedSections.length > 0) {
            const lastPassedSection = passedSections.reduce((prev, curr) => (curr.top > prev.top ? curr : prev))
            activeSectionId = lastPassedSection.id
          } else {
            // Nếu không có section nào đã đi qua, vẫn highlight section đang hiển thị gần nhất
            activeSectionId = closestVisible.id
          }
        }
      } else {
        // Nếu không có section nào đang hiển thị, tìm section đã đi qua gần nhất
        const passedSections = offsets.filter(offset => offset.top < 0)
        if (passedSections.length > 0) {
          const lastPassedSection = passedSections.reduce((prev, curr) => (curr.top > prev.top ? curr : prev))
          activeSectionId = lastPassedSection.id
        }
      }

      if (activeSectionId && activeSectionId !== activeId) {
        setActiveId(activeSectionId)
      }
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [items, activeId])

  return (
    <div className="flex flex-col gap-4">
      {items
        .filter(item => item.title && item.title.trim() && item.title !== "\u00A0")
        .map(item => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className={cn(
              "text-base leading-6 transition-colors",
              activeId === item.id ? "text-[#0B9444]" : "text-[#4F4F4F] hover:text-[#0B9444]",
              className,
            )}
            style={{
              paddingLeft: `${(item.level - 2) * 10}px`,
            }}
            onClick={e => {
              e.preventDefault()
              const targetElement = document.getElementById(item.id)
              if (targetElement) {
                const yOffset = -90
                const yPosition = targetElement.getBoundingClientRect().top + window.pageYOffset + yOffset
                window.scrollTo({
                  top: yPosition,
                  behavior: "smooth",
                })
              }
            }}
          >
            {item.title}
          </a>
        ))}
    </div>
  )
}

export default TableContent
